import React from 'react';
import type {
  LayoutComponentProps,
  Options,
  AppBoxSkin,
} from './app-box.types';
import { Box, Text } from '@wix/design-system';
import style from './app-box.module.scss';
import { AppIcon } from '../app-icon';
import { useEssentials } from '../../contexts/essentials-context';
import { AppReviewsInfo } from './app-box-elements/app-reviews-Info';
import { CTAButton } from './app-box-elements/cta-button';
import {
  NarrowAppBox,
  NarrowSlimAppBox,
} from './app-box-layouts/narrow-app-box';
import {
  RegularAppBox,
  RegularSlimAppBox,
} from './app-box-layouts/regular-app-box';
import { WideAppBox } from './app-box-layouts/wide-app-box';
import { AppInfoBadge } from '../app-info-badge';
import type { PricingBadgeProps } from '../pricing-badge';
import { appBoxDataHooks } from './data-hooks';
import { useSiteContext } from '../../contexts/site-context';
import { shouldShowPremiumRequired } from '../app-info-badge/app-info-badge.util';
import { AppBadges } from '../badges/app-badges';
import type { BaseApp } from '../../types';
import { isAppWithBenefits } from '@wix/app-market-services';

export interface AppBoxProps {
  appData: BaseApp;
  onClick?(): void;
  options?: Options;
  href?: string;
  languageCode?: string;
  skin?: AppBoxSkin;
}

export function AppBox({
  appData,
  onClick,
  options = {},
  href,
  languageCode = 'en',
  skin = 'default',
}: AppBoxProps) {
  const {
    slug,
    icon,
    name,
    company,
    shortDescription,
    reviews,
    installation,
    pricing,
    appBadges = [],
  } = appData;
  const {
    showCTA = false,
    showCompanyName = false,
    showRating = true,
    showPricing = true,
    showBorder = true,
    showBadgesVertical = true,
  } = options;

  const { t } = useEssentials();
  const { isPremiumSite } = useSiteContext();

  const renderTopBadge = () => {
    return (
      <Box
        direction={showBadgesVertical ? 'vertical' : 'horizontal'}
        align="right"
        gap={showBadgesVertical ? '3px' : '6px'}
      >
        <AppBadges
          isInstalled={Boolean(installation?.isInstalled)}
          appName={name ?? ''}
          appBadges={appBadges}
          languageCode={languageCode}
          maxCount={skin === 'slim' ? 1 : 2}
        />
      </Box>
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onClick?.();
    }
  };

  const appBoxLayoutProps: LayoutComponentProps = {
    appIcon: (
      <AppIcon
        iconUrl={icon ?? ''}
        appName={name ?? ''}
        className={style.icon}
        size={skin === 'slim' ? 'medium' : 'small'}
      />
    ),
    appName: (
      <Text size="small" weight="bold" ellipsis={skin === 'slim'}>
        {name}
      </Text>
    ),
    companyName: showCompanyName ? (
      <Text secondary size="tiny" weight="thin">
        {t('solutionBox.solutionDetails.companyName', {
          company: company?.name,
        })}
      </Text>
    ) : null,
    appTeaser: (
      <Text
        size="small"
        weight="thin"
        secondary={skin === 'slim'}
        maxLines={skin === 'slim' ? 2 : undefined}
        ellipsis={skin === 'slim'}
      >
        {shortDescription}
      </Text>
    ),
    ctaButton: showCTA ? (
      <CTAButton onClick={() => onClick?.()}>
        {installation?.isInstalled
          ? t('apps.grid.cta.button.installed')
          : t('apps.grid.cta.button')}
      </CTAButton>
    ) : null,
    reviewsInfo:
      showRating && reviews ? (
        <AppReviewsInfo reviews={reviews} skin={skin} />
      ) : null,
    bottomBadge: (
      <AppInfoBadge
        isAppInstalled={!!installation?.isInstalled}
        isAppBuiltIn={!!installation?.isBuiltIn}
        isAppSetupIncomplete={!!installation?.isSetupIncomplete}
        isAppWithBenefits={isAppWithBenefits(appBadges)}
        pricing={pricing as PricingBadgeProps}
        options={{
          showPricing,
          showPremiumRequired: shouldShowPremiumRequired(
            !!installation?.requirements?.premiumSite,
            isPremiumSite,
          ),
        }}
        skin={skin}
      />
    ),
    topBadge: renderTopBadge(),
  };

  return (
    <div
      data-hook={appBoxDataHooks.container}
      className={`${style.appBoxContainer} ${
        skin === 'slim' ? style.skinSlim : style.skinDefault
      }`}
    >
      <div
        data-hook={slug}
        className={`${style.appBox} ${!showBorder ? style.noBorder : ''}`}
        onClick={(e) => {
          onClick?.();
          e.preventDefault();
        }}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        style={
          {
            ...(skin === 'slim' && {
              padding: '18px 12px 12px 15px',
            }),
          } as React.CSSProperties
        }
      >
        <a className={style.link} href={href}>
          {skin === 'slim' ? (
            <>
              <NarrowSlimAppBox
                {...appBoxLayoutProps}
                className={style.narrowSlimLayout}
              />
              <RegularSlimAppBox
                {...appBoxLayoutProps}
                className={style.regularSlimLayout}
              />
            </>
          ) : (
            <>
              <NarrowAppBox
                {...appBoxLayoutProps}
                className={style.narrowLayout}
              />
              <RegularAppBox
                {...appBoxLayoutProps}
                className={style.regularLayout}
              />
              <WideAppBox {...appBoxLayoutProps} className={style.wideLayout} />
            </>
          )}
        </a>
      </div>
    </div>
  );
}
